// frontend/src/pages/EnquadramentoPages.js
import React, { useEffect, useMemo, useState } from "react";
import "./Complemento.css";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";

import ThemeProvider from "../theme/TemaMuiProvider";
import { Box, Button, TextField, TablePagination, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import muiTheme from "../theme/muiTheme";

async function parseJsonSafe(res) {
  const text = await res.text();
  try {
    const data = text ? JSON.parse(text) : null;
    return { ok: res.ok, status: res.status, data, text };
  } catch {
    return { ok: res.ok, status: res.status, data: null, text };
  }
}

function EnquadramentoPages() {
  const [enquadramentos, setEnquadramentos] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [novoEnquadramento, setNovoEnquadramento] = useState("");
  const [editandoId, setEditandoId] = useState(null);
  const [nomeEditado, setNomeEditado] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [enquadramentosPorPagina, setEnquadramentosPorPagina] = useState(5);
  const [loading, setLoading] = useState(true);

  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("enquadramentoGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("enquadramentoGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  const [erroApi, setErroApi] = useState("");

  const token = localStorage.getItem("token");

  const tipoUsuario = useMemo(() => {
    if (!token) return "";
    try {
      const decoded = jwtDecode(token);
      return decoded.tipo
        ?.toString()
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    } catch {
      return "";
    }
  }, [token]);

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  useEffect(() => {
    let alive = true;

    const carregar = async () => {
      setErroApi("");

      if (!podeVisualizar) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const tk = localStorage.getItem("token");

        const res = await fetch("/api/enquadramentos", {
          headers: { Authorization: `Bearer ${tk}` },
        });

        const { ok, status, data, text } = await parseJsonSafe(res);

        if (!ok) {
          const msg = data?.erro || data?.error || data?.mensagem || `Erro ao buscar enquadramentos (HTTP ${status}).`;

          throw new Error(`${msg}${text ? ` | ${text.slice(0, 180)}` : ""}`);
        }

        const lista = Array.isArray(data) ? data : Array.isArray(data?.enquadramentos) ? data.enquadramentos : [];

        if (alive) {
          setEnquadramentos(lista);
          setLoading(false);
        }
      } catch (err) {
        console.error("Erro ao buscar Enquadramentos:", err);
        if (alive) {
          setEnquadramentos([]);
          setErroApi("Não foi possível carregar os enquadramentos. Verifique se o backend está online e se a rota /api/enquadramentos está funcionando.");
          setLoading(false);
        }
      }
    };

    carregar();

    return () => {
      alive = false;
    };
  }, [podeVisualizar]);

  const handleCadastrar = async (e) => {
    e.preventDefault();
    setErroApi("");

    if (!podeCadastrar) {
      alert("Você não tem permissão para cadastrar enquadramentos.");
      return;
    }

    const nome = novoEnquadramento.trim();
    if (!nome) {
      alert("Preencha o campo Enquadramento.");
      return;
    }

    const nomeFormatado = nome.toLowerCase();
    const jaExiste = enquadramentos.some((c) => (c?.nome ?? "").trim().toLowerCase() === nomeFormatado);
    if (jaExiste) {
      alert("Esse Enquadramento já está cadastrado");
      return;
    }

    try {
      const tk = localStorage.getItem("token");

      const res = await fetch("/api/enquadramentos", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tk}`,
        },
        body: JSON.stringify({ nome: nome.toUpperCase() }),
      });

      const { ok, status, data, text } = await parseJsonSafe(res);

      if (status === 403) {
        alert("Você não tem permissão para cadastrar.");
        return;
      }

      if (!ok) {
        const msg = data?.erro || data?.error || data?.mensagem || `Erro ao cadastrar (HTTP ${status}).`;
        throw new Error(`${msg}${text ? ` | ${text.slice(0, 180)}` : ""}`);
      }

      const novoItem = {
        id: data.id,
        nome: (data.nome ?? nome).toUpperCase(),
      };

      setEnquadramentos((prev) => [novoItem, ...prev]);

      setPaginaAtual(1);
      setNovoEnquadramento("");
    } catch (err) {
      console.error("Erro ao cadastrar enquadramento:", err);
      setErroApi("Falha ao cadastrar enquadramento. Verifique o backend e tente novamente.");
    }
  };

  const handleEdit = (item) => {
    setEditandoId(item.id);
    setNomeEditado(item.nome ?? "");
  };

  const handleSaveEdit = async (id) => {
    setErroApi("");

    if (!podeEditar) {
      alert("Você não tem permissão para editar enquadramentos.");
      return;
    }

    const nome = (nomeEditado ?? "").trim();
    if (!nome) {
      alert("Informe o nome do Enquadramento.");
      return;
    }

    try {
      const tk = localStorage.getItem("token");

      const res = await fetch(`/api/enquadramentos/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tk}`,
        },
        body: JSON.stringify({ nome }),
      });

      const { ok, status, data, text } = await parseJsonSafe(res);

      if (status === 403) {
        alert("Você não tem permissão para editar.");
        return;
      }

      if (!ok) {
        const msg = data?.erro || data?.error || data?.mensagem || `Erro ao editar (HTTP ${status}).`;
        throw new Error(`${msg}${text ? ` | ${text.slice(0, 180)}` : ""}`);
      }

      setEnquadramentos((prev) => prev.map((c) => (c.id === id ? { ...c, nome } : c)));
      setEditandoId(null);
      setNomeEditado("");
    } catch (err) {
      console.error("Erro ao editar enquadramento:", err);
      setErroApi("Falha ao editar enquadramento. Verifique o backend e tente novamente.");
    }
  };

  const handleDelete = async (id) => {
    setErroApi("");

    if (!podeExcluir) {
      alert("Você não tem permissão para excluir enquadramentos.");
      return;
    }

    const confirmar = window.confirm("Deseja realmente excluir este enquadramento?");
    if (!confirmar) return;

    try {
      const tk = localStorage.getItem("token");

      const res = await fetch(`/api/enquadramentos/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${tk}` },
      });

      const { ok, status, data, text } = await parseJsonSafe(res);

      if (status === 403) {
        alert("Você não tem permissão para excluir.");
        return;
      }

      if (!ok) {
        const msg = data?.erro || data?.error || data?.mensagem || `Erro ao excluir (HTTP ${status}).`;
        throw new Error(`${msg}${text ? ` | ${text.slice(0, 180)}` : ""}`);
      }

      setEnquadramentos((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error("Erro ao excluir enquadramento:", err);
      setErroApi("Falha ao excluir enquadramento. Verifique o backend e tente novamente.");
    }
  };

  const enquadramentosFiltrados = useMemo(() => {
    const f = filtro.toLowerCase();
    return (enquadramentos || []).filter((c) => (c?.nome ?? "").toLowerCase().includes(f));
  }, [enquadramentos, filtro]);

  const indexInicial = (paginaAtual - 1) * enquadramentosPorPagina;
  const indexFinal = indexInicial + enquadramentosPorPagina;
  const enquadramentosPaginados = enquadramentosFiltrados.slice(indexInicial, indexFinal);
  const totalPaginas = Math.ceil(enquadramentosFiltrados.length / enquadramentosPorPagina) || 1;

  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(1);
  }, [paginaAtual, totalPaginas]);

  const handleCSV = () => {
    const BOM = "\uFEFF";
    const header = ["Enquadramento"];

    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const linhas = (enquadramentosFiltrados || []).map((c) => [c?.nome ?? ""].map(csvEscape));

    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");

    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "enquadramentos.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  if (!podeVisualizar) {
    return (
      <ThemeProvider theme={muiTheme}>
        <div className="complemento-container">
          <h2>Enquadramento</h2>
          <p className="erro">🔒 Você não tem permissão para visualizar esta tela.</p>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="complemento-container">
        <style>{`
        .campo-formulario-padrao,
        .campo-formulario-padrao:focus,
        .campo-formulario-padrao:active,
        .campo-formulario-padrao:read-only,
        .campo-formulario-padrao:disabled {
          background: transparent !important;
          background-color: transparent !important;
          color: var(--ig-t-ffffff) !important;
          box-shadow: none !important;
          -webkit-text-fill-color: var(--ig-t-ffffff) !important;
          opacity: 1 !important;
        }

        .campo-formulario-padrao::placeholder {
          color: var(--ig-t-255-255-255-a072) !important;
          -webkit-text-fill-color: var(--ig-t-255-255-255-a072) !important;
        }

        .campo-formulario-padrao::-webkit-calendar-picker-indicator {
          filter: var(--ig-x-11pexw5);
          opacity: 0.9;
          cursor: pointer;
        }
      `}</style>

        <h2>Enquadramento</h2>

        {erroApi && (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">⚠️ {erroApi}</p>
          </div>
        )}

        <div className="bloco-linha">
          <TextField
            label="Buscar Enquadramento"
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setPaginaAtual(1);
            }}
            fullWidth
            size="small"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" sx={{ color: "var(--ig-t-9ca3af)" }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 52,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-input": {
                padding: "14px 14px",
              },
              "& .MuiInputLabel-root": {
                transform: "translate(14px, 15px) scale(1)",
              },
              "& .MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            }}
          />

          <div className="filtro-botoes">
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => {
                setFiltro("");
                setPaginaAtual(1);
              }}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={async () => {
                const texto = enquadramentosFiltrados.map((c) => c.nome).join(", ");
                if (!texto.trim()) {
                  alert("Nada para copiar (lista vazia).");
                  return;
                }
                const ok = await copiarTexto(texto);
                alert(ok ? "Copiado!" : "Não foi possível copiar.");
              }}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <i className="fas fa-copy" style={{ marginRight: 6 }}></i>
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </div>
        </div>

        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o Grid"
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o Grid"
            >
              ↗ Expandir
            </button>
          )}
        </div>

        {!gridMinimizado && (
          <div className="bloco-lista">
            {loading ? (
              <p>Carregando Enquadramentos...</p>
            ) : enquadramentosFiltrados.length === 0 ? (
              <p className="erro">Nenhum enquadramento cadastrado.</p>
            ) : (
              <>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: "82%" }}>Enquadramento</th>
                      <th style={{ width: "300px", textAlign: "center" }}>Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {enquadramentosPaginados.map((item) => (
                      <tr key={item.id}>
                        <td className="nome-complemento">
                          {editandoId === item.id ? (
                            <input
                              value={nomeEditado}
                              onChange={(e) => setNomeEditado(e.target.value.toUpperCase())}
                              autoFocus
                              style={{
                                width: "100%",
                                boxSizing: "border-box",
                                backgroundColor: "var(--ig-f-1f2937)",
                                border: "2px solid #1989af",
                                borderRadius: "6px",
                                padding: "10px 12px",
                                color: "var(--ig-t-ffffff)",
                                fontWeight: "600",
                                boxShadow: "0 0 10px rgba(34, 116, 197, 0.35)",
                              }}
                            />
                          ) : (
                            item.nome
                          )}
                        </td>

                        <td
                          className="botoes-acao"
                          style={{
                            width: "300px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "10px",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {editandoId === item.id ? (
                              <Button
                                size="small"
                                variant="contained"
                                type="button"
                                startIcon={<i className="fas fa-floppy-disk" />}
                                onClick={() => handleSaveEdit(item.id)}
                                sx={{
                                  minWidth: 118,
                                  height: 36,
                                  borderRadius: 1,
                                  fontWeight: 600,
                                  fontSize: "0.85rem",
                                  textTransform: "none",
                                  backgroundColor: "#2e7d32",
                                  color: "#fff",
                                  "&:hover": { backgroundColor: "#1b5e20" },
                                }}
                              >
                                Salvar
                              </Button>
                            ) : (
                              <>
                                {podeEditar && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    type="button"
                                    startIcon={<i className="fas fa-pen" />}
                                    onClick={() => handleEdit(item)}
                                    sx={{
                                      minWidth: 118,
                                      height: 36,
                                      borderRadius: 1,
                                      fontWeight: 600,
                                      fontSize: "0.85rem",
                                      textTransform: "none",
                                      backgroundColor: "#1998a8",
                                      color: "#fff",
                                      "&:hover": { backgroundColor: "#177f8d" },
                                    }}
                                  >
                                    Editar
                                  </Button>
                                )}

                                {podeExcluir && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    type="button"
                                    startIcon={<i className="fas fa-trash" />}
                                    onClick={() => handleDelete(item.id)}
                                    sx={{
                                      minWidth: 118,
                                      height: 36,
                                      borderRadius: 1,
                                      fontWeight: 600,
                                      fontSize: "0.85rem",
                                      textTransform: "none",
                                      backgroundColor: "#d32f2f",
                                      color: "#fff",
                                      "&:hover": { backgroundColor: "#b71c1c" },
                                    }}
                                  >
                                    Excluir
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <TablePagination
                  component="div"
                  count={enquadramentosFiltrados.length}
                  page={paginaAtual - 1}
                  onPageChange={(_, novaPagina) => setPaginaAtual(novaPagina + 1)}
                  rowsPerPage={enquadramentosPorPagina}
                  onRowsPerPageChange={(e) => {
                    setEnquadramentosPorPagina(parseInt(e.target.value, 10));
                    setPaginaAtual(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage="Enquadramentos por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                  slotProps={{
                    select: {
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            bgcolor: "var(--ig-f-1e1e1e)",
                            color: "var(--ig-t-ffffff)",
                            "& .MuiMenuItem-root": {
                              color: "var(--ig-t-ffffff)",
                            },
                            "& .MuiMenuItem-root.Mui-selected": {
                              bgcolor: "var(--ig-f-2b2b2b)",
                            },
                            "& .MuiMenuItem-root:hover": {
                              bgcolor: "var(--ig-f-333333)",
                            },
                          },
                        },
                      },
                    },
                  }}
                  sx={{
                    fontSize: "0.8rem",
                    minHeight: 42,
                    color: "var(--ig-t-ffffff)",
                    borderTop: "1px solid var(--ig-b-444444)",
                    "& .MuiTablePagination-toolbar": { minHeight: 42 },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                      fontSize: "0.8rem",
                    },
                    "& .MuiTablePagination-selectIcon": { color: "var(--ig-t-ffffff)" },
                    "& .MuiTablePagination-actions button": {
                      color: "var(--ig-t-ffffff)",
                      width: "auto",
                    },
                    "& .MuiTablePagination-actions": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                />
              </>
            )}
          </div>
        )}

        {!podeCadastrar && (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você possui acesso apenas de visualização.</p>
          </div>
        )}

        {podeCadastrar && (
          <div className="bloco-formulario">
            <form onSubmit={handleCadastrar} className="complemento-form">
              <label htmlFor="enquadramento">Enquadramento</label>

              <input
                id="enquadramento"
                value={novoEnquadramento}
                onChange={(e) => setNovoEnquadramento(e.target.value.toUpperCase())}
                placeholder="Digite o nome do Enquadramento"
                required
                className="campo-formulario-padrao"
              />

              <Button
                type="submit"
                variant="contained"
                startIcon={<i className="fas fa-floppy-disk" />}
                sx={{
                  width: 240,
                  minWidth: 240,
                  maxWidth: 240,

                  height: 52,
                  minHeight: 52,

                  alignSelf: "center",

                  textTransform: "none",
                  fontWeight: 600,

                  color: "#fff",
                  backgroundColor: "#1998a8",

                  "&:hover": {
                    backgroundColor: "#177f8d",
                  },
                }}
              >
                Cadastrar
              </Button>
            </form>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default EnquadramentoPages;
